import React, { FunctionComponent, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { Tab, Tabs } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IAppState } from "@multiweb/types/react";
import { createUpdateTransitionAction } from "../../../actions";
import { TransitionEditDialogTabsIndex } from "../../../enums";
import { LOC_ACTIONS, LOC_PROPERTIES } from "../../../locKeys";
import { getTransitionByIndex, isEditDialogHidden } from "../../../selectors";
import { IAction, ITransition, WorkflowEditorProps } from "../../../types";
import { UiTransition } from "../../../UiTransition";
import { UiWorkflow } from "../../../UiWorkflow";
import { getUiWorkflowMutableObject } from "../../../utils";
import { EditDialog } from "../EditDialog";
import { ActionsTab, TransitionPropertiesTab } from "./tabs";
import { createNewActions } from "./utils";

type TransitionEditDialogComponentProps = {
    readonly transition: ITransition;
    readonly isHidden: boolean;
    readonly uiWorkflow: UiWorkflow;
    readonly defaultTabIndex?: TransitionEditDialogTabsIndex;
    readonly selectedRecordIndex?: number;
    readonly updateTransition: (index: number, newTransition: ITransition) => void;
    readonly onCloseEdit: () => void;
    readonly propertiesText: string;
    readonly actionsText: string;
} & WorkflowEditorProps;

const TransitionEditDialogComponent: FunctionComponent<TransitionEditDialogComponentProps> = ({ hostKey, transition, isHidden, uiWorkflow, defaultTabIndex = TransitionEditDialogTabsIndex.Properties, selectedRecordIndex = 0, updateTransition, onCloseEdit, propertiesText, actionsText }) => {
    const { actions, picassoIndex } = transition;

    const [touched, setTouched] = useState(false);

    const handleUpdateTransition = (newTransition: ITransition) => {
        updateTransition(picassoIndex, newTransition);
        !touched && setTouched(true);
    };

    const updateActions = (newActions: readonly IAction[]) => {
        handleUpdateTransition({ ...transition, actions: newActions });
    };

    const onCancelClick = () => {
        const { transitions } = uiWorkflow.workflowModel;
        handleUpdateTransition(transitions[picassoIndex]);
    };

    const onSaveClick = async () => {
        const wfTransition = (uiWorkflow.picassoModel.links[picassoIndex] as UiTransition).transition;

        // Vyseparováno tady nahoře, aby pokud dojde k vyhození výjimky, tak se nové hodnoty neuložily do objektu aktivity
        const newActions = await createNewActions(transition.actions);

        wfTransition.name = transition.name;
        wfTransition.condition = transition.condition;
        wfTransition.actions = newActions;
    };

    return (
        <EditDialog width={900} height={700} {...{ hostKey, isHidden, touched, onCloseEdit, onSaveClick, onCancelClick, setTouched }}>
            <Tabs {...{ defaultTabIndex }}>
                <Tab tabName={propertiesText}>
                    <TransitionPropertiesTab updateTransition={handleUpdateTransition} {...{ transition, hostKey }} />
                </Tab>
                <Tab tabName={actionsText}>
                    <ActionsTab {...{ hostKey, actions, selectedRecordIndex, updateActions }} />
                </Tab>
            </Tabs>
        </EditDialog>
    );
};

type TransitionEditDialogProps = {
    readonly picassoIndex: number;
} & Pick<TransitionEditDialogComponentProps, "onCloseEdit" | "hostKey" | "defaultTabIndex" | "selectedRecordIndex">;

type StateProps = Pick<TransitionEditDialogComponentProps, "transition" | "isHidden" | "propertiesText" | "actionsText">;

const mapStateToProps: MapStateToProps<StateProps, TransitionEditDialogProps, IAppState> = (state, { hostKey, picassoIndex }) => ({
    transition: getTransitionByIndex(state, hostKey, picassoIndex),
    isHidden: isEditDialogHidden(state, hostKey),
    propertiesText: getPrimaryText(state, LOC_PROPERTIES) ?? "Vlastnosti",
    actionsText: getPrimaryText(state, LOC_ACTIONS) ?? "Akce",
});

type DispatchProps = Pick<TransitionEditDialogComponentProps, "updateTransition">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, TransitionEditDialogProps> = (dispatch, { hostKey }) => ({
    updateTransition: (index, newTransition) => dispatch(createUpdateTransitionAction(hostKey, index, newTransition)),
});

const mergeProps: MergeProps<StateProps, DispatchProps, TransitionEditDialogProps, TransitionEditDialogComponentProps> = (
    stateProps,
    dispatchProps,
    ownProps,
) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    uiWorkflow: getUiWorkflowMutableObject(ownProps.hostKey),
});

export const TransitionEditDialog = connect(mapStateToProps, mapDispatchToProps, mergeProps)(TransitionEditDialogComponent);
